import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { client } from "../sanity";
import { PortableText } from "@portabletext/react";
import SEO from "../components/SEO";

const getPlainText = (children = []) =>
  children.map((child) => child.text || "").join("");

const isSectionLabel = (text) => {
  const trimmed = text.trim();
  return (
    trimmed.length > 4 &&
    trimmed.length < 84 &&
    !/[.!?]$/.test(trimmed) &&
    trimmed.split(" ").length <= 10
  );
};

const articleComponents = {
  block: {
    normal: ({ children, value }) => {
      const text = getPlainText(value.children).trim();
      const canSplitText = value.children?.every(
        (child) =>
          child._type === "span" &&
          (!child.marks || child.marks.length === 0)
      );
      const paragraphs = canSplitText
        ? text.split(/\n+/).map((paragraph) => paragraph.trim()).filter(Boolean)
        : [];

      if (paragraphs.length > 1) {
        return (
          <>
            {paragraphs.map((paragraph, index) =>
              isSectionLabel(paragraph) ? (
                <h2
                  key={`${paragraph}-${index}`}
                  className="mt-12 mb-4 font-serif text-3xl font-bold leading-tight tracking-[-0.04em] text-[#111712] md:text-4xl"
                >
                  {paragraph}
                </h2>
              ) : (
                <p
                  key={`${paragraph}-${index}`}
                  className={`mb-6 text-[1.05rem] leading-8 text-[#4d5951] md:text-lg md:leading-9 ${
                    index === 0 ? "text-xl font-medium leading-9 text-[#111712] md:text-2xl md:leading-10" : ""
                  }`}
                >
                  {paragraph}
                </p>
              )
            )}
          </>
        );
      }

      return (
        <p className="mb-6 text-[1.05rem] leading-8 text-[#4d5951] md:text-lg md:leading-9">
          {children}
        </p>
      );
    },
    h2: ({ children }) => (
      <h2 className="mt-14 mb-5 font-serif text-3xl font-bold leading-tight tracking-[-0.04em] text-[#111712] md:text-4xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-10 mb-4 font-serif text-2xl font-semibold leading-tight tracking-[-0.03em] text-[#111712]">
        {children}
      </h3>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-10 border-l-4 border-[#45695a] bg-[#bfd4c4]/35 px-6 py-5 font-serif text-xl italic leading-8 text-[#344039] md:text-2xl md:leading-9">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-8 space-y-3 border-l border-[#45695a]/30 pl-6 text-lg leading-8 text-[#4d5951]">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="my-8 list-decimal space-y-3 pl-7 text-lg leading-8 text-[#4d5951] marker:text-[#45695a]">
        {children}
      </ol>
    ),
  },
  listItem: ({ children }) => <li className="pl-2">{children}</li>,
  marks: {
    link: ({ children, value }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-[#45695a] underline decoration-[#45695a]/40 underline-offset-4 hover:text-[#111712]"
      >
        {children}
      </a>
    ),
  },
};

export default function Post() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    client
      .fetch(
        `*[_type == "post" && slug.current == $slug][0]{
          title,
          publishedAt,
          mainImage{
            asset->{url}
          },
          body,
          author->{
            name
          },
          categories[]->{
            title
          }
        }`,
        { slug }
      )
      .then((data) => setPost(data))
      .catch(console.error);
  }, [slug]);

  if (!post) {
    return (
      <section className="min-h-screen bg-[#eeeee8] px-5 py-28 text-[#111712] sm:px-8 md:px-12">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-[#4d5951]">Loading article...</p>
        </div>
      </section>
    );
  }

  const readingTime = Math.max(
    1,
    Math.ceil(
      post.body
        ?.map((block) => getPlainText(block.children))
        .join(" ")
        .split(/\s+/)
        .filter(Boolean).length / 220
    )
  );

  return (
    <>
      <SEO
        title={`${post.title} | Jisedai Blog`}
        description="Read insights, updates, and practical perspectives from Jisedai."
        image={post.mainImage?.asset?.url}
        url={`https://www.jisedai.africa/blog/${slug}`}
        type="article"
      />

      <section className="relative min-h-screen overflow-hidden bg-[#eeeee8] px-5 py-28 text-[#111712] sm:px-8 md:px-12">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/4 top-10 h-64 w-64 rounded-full bg-[#bfd4c4]/45 blur-3xl" />
          <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-[#d6e85c]/30 blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto">
          <Link
            to="/blog"
            className="mb-8 inline-block font-bold text-[#45695a] transition hover:text-[#111712]"
          >
            &larr; Back to Blog
          </Link>

          <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-[#667269]">
            {post.publishedAt && (
              <span>
                {new Date(post.publishedAt).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            )}
            {post.author?.name && <span>&bull; {post.author.name}</span>}
            <span>&bull; {readingTime} min read</span>
          </div>

          <h1 className="mb-6 max-w-4xl font-serif text-5xl leading-[0.94] tracking-[-0.055em] text-[#111712] md:text-7xl">
            {post.title}
          </h1>

          {post.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.categories.map((cat, i) => (
                <span
                  key={i}
                  className="rounded-full border border-[#111712]/10 bg-white/55 px-3 py-1 text-xs text-[#45695a]"
                >
                  {cat.title}
                </span>
              ))}
            </div>
          )}

          {post.mainImage?.asset?.url && (
            <img
              src={post.mainImage.asset.url}
              alt={post.title}
              className="mb-10 h-[280px] w-full rounded-3xl border border-[#111712]/15 object-cover shadow-xl shadow-[#45695a]/10 md:h-[420px]"
            />
          )}

          <article className="mx-auto max-w-3xl rounded-3xl border border-[#111712]/15 bg-[#f7f7f2] px-6 py-10 shadow-xl shadow-[#45695a]/10 md:px-12 md:py-14">
            <div className="mb-10 flex items-center gap-3 border-b border-[#111712]/10 pb-6 text-[11px] font-bold uppercase tracking-[0.2em] text-[#45695a]">
              <span className="h-2 w-2 rounded-full bg-[#d6e85c]" />
              Jisedai Field Notes
            </div>
            <PortableText value={post.body} components={articleComponents} />
          </article>
        </div>
      </section>
    </>
  );
}
